import { useEffect, useId, useRef, useState } from "react";
import { KeyRound, Lock, Mail, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import { api } from "../../../services/api";
import { TokenManager } from "../../../services/apiClient";
import {
  useLogin,
  useGoogleAuth,
  useGithubAuth,
  useMicrosoftAuth,
  useForgotPassword,
  useInitiateSSO,
  useVerifyDomain,
  useVerify2FA,
  useResend2FA,
  useRequestAccountUnlock,
  useSendMagicLink,
  useAuthenticatePasskey,
} from "../../../hooks/useAPI";
import {
  AuthShell,
  AuthBanner,
  AuthInput,
  AuthButton,
  OAuthButtonRow,
  Crossfade,
  ResendButton,
} from "./_primitives";

type AuthView = "default" | "magic-link-sent" | "reset-password-sent" | "mfa-required";

// SSO/OAuth failure-code vocabulary surfaced via `?error=<code>` query
// param from the backend OIDC/SAML callback. Each code appears exactly
// once. Recovery actions intentionally favor "Back to sign in" so the
// compliance officer never sees a raw error code.
const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_idp_error: "Your identity provider declined the request. Contact your IT administrator.",
  sso_missing_params: "The SSO callback was missing required parameters. Start the sign-in again.",
  sso_invalid_state: "Your sign-in session expired. Start the SSO flow again.",
  sso_state_expired: "SSO session expired. Start the sign-in again.",
  sso_missing_state: "The SSO callback was missing required state. Start the sign-in again.",
  sso_not_configured: "SSO is not configured for this organisation. Contact your IT administrator or sign in with email + password.",
  sso_exchange_failed: "The SSO handshake did not complete. Start the sign-in again.",
  sso_nonce_mismatch: "Your SSO session expired between the redirect and callback. Start the sign-in again.",
  sso_invalid_code: "The authorization code from your identity provider was already used or expired. Start the sign-in again.",
  sso_invalid_credentials: "We could not authenticate to your identity provider. Ask your administrator to verify the Client Secret in Settings.",
  sso_idp_timeout: "Your identity provider did not respond in time. Try again, or check the IdP status page if it persists.",
  sso_idp_5xx: "Your identity provider returned a server error. Try again or contact your IT administrator.",
  sso_invalid_signature: "The response signature from your identity provider could not be verified. Ask your administrator to verify the IdP signing certificate is current.",
  sso_misconfigured: "SSO is enabled but not fully configured. Ask your administrator to complete the SSO setup in Settings.",
  sso_no_email: "Your identity provider did not return an email address. Check the IdP configuration.",
  sso_no_profile: "Your identity provider returned no profile information. Contact your IT administrator.",
  sso_domain_mismatch: "Your email domain does not match this organisation's SSO configuration.",
  sso_account_suspended: "Your account has been suspended. Contact your organisation administrator.",
  sso_server_error: "An internal error occurred during SSO. Try again, or contact support@adjudon.com.",
  sso_mfa_required_no_enroll: "Your organisation requires multi-factor authentication on top of SSO. Sign in once with a password to enrol your authenticator, then retry SSO.",
  sso_assertion_invalid: "The response from your identity provider could not be verified. Try again or contact your IT administrator.",
  google_failed: "Google sign-in did not complete. Try again, or use email + password instead.",
  github_no_email: "GitHub did not provide an email address. Make your GitHub email public or use a different sign-in method.",
  microsoft_failed: "Microsoft sign-in did not complete. Try again, or use email + password instead.",
  oauth_failed: "Sign-in did not complete. Try again or use a different sign-in method.",
};

const isValidEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

export function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [authView, setAuthView] = useState<AuthView>("default");

  // 2FA state
  const [tempToken, setTempToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState("");
  const mfaInputRef = useRef<HTMLInputElement | null>(null);
  const mfaErrorId = useId();

  // Account-locked state
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [unlockEmailSent, setUnlockEmailSent] = useState(false);

  // SSO domain auto-detect
  type SSOProviderHint = { id: string; name: string };
  const [ssoHint, setSsoHint] = useState<{ providers: SSOProviderHint[] } | null>(null);
  const ssoHintDomainRef = useRef<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const login = useLogin();
  const googleAuth = useGoogleAuth();
  const githubAuth = useGithubAuth();
  const microsoftAuth = useMicrosoftAuth();
  const forgotPassword = useForgotPassword();
  const initiateSSO = useInitiateSSO();
  const verifyDomain = useVerifyDomain();
  const verify2FA = useVerify2FA();
  const resend2FA = useResend2FA();
  const requestUnlock = useRequestAccountUnlock();
  const magicLink = useSendMagicLink();
  const passkeyAuth = useAuthenticatePasskey();

  // Browser support detection — `PublicKeyCredential` is the WebAuthn
  // entry point. Older browsers + corporate-WebView contexts may not
  // expose it; we hide the passkey button rather than fail at click time.
  const [passkeySupported, setPasskeySupported] = useState(false);
  useEffect(() => {
    if (typeof window !== "undefined" && "PublicKeyCredential" in window) {
      setPasskeySupported(true);
    }
  }, []);

  // Passkey Conditional-UI autofill — Safari 16+, Chrome 108+. The
  // browser pops the saved passkey as a suggestion inside the email
  // input's native autofill chooser. When the user picks it we run
  // the same success-path as an explicit click on the passkey button.
  // The promise is long-lived: it stays pending until the user picks
  // an option or the AbortController fires. We cancel on unmount, on
  // explicit passkey-button click, and on password-form submit so the
  // browser's autofill UI doesn't compete with the user's own action.
  const conditionalAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (!passkeySupported || authView !== "default") return;
    let cancelled = false;
    const start = async () => {
      try {
        const PKC = (window as unknown as { PublicKeyCredential?: { isConditionalMediationAvailable?: () => Promise<boolean> } }).PublicKeyCredential;
        if (!PKC?.isConditionalMediationAvailable) return;
        const available = await PKC.isConditionalMediationAvailable();
        if (!available || cancelled) return;
        const controller = new AbortController();
        conditionalAbortRef.current = controller;
        const options = await api.getPasskeyAuthenticationOptions();
        if (cancelled) return;
        const credential = await navigator.credentials.get({
          publicKey: options as unknown as PublicKeyCredentialRequestOptions,
          mediation: "conditional" as CredentialMediationRequirement,
          signal: controller.signal,
        });
        if (cancelled || !credential) return;
        const response = await api.verifyPasskeyAuthentication(credential);
        const tok = (response as { token?: string })?.token;
        if (tok) {
          TokenManager.setAccessToken(tok);
          toast.success(t("login.toasts.signedIn"), { description: t("login.toasts.signedInDescription") });
          navigate("/dashboard");
        }
      } catch {
        // AbortError / NotAllowedError / network errors — silent. Conditional
        // UI is best-effort; the explicit passkey button + password form
        // remain the user's primary paths.
      }
    };
    void start();
    return () => {
      cancelled = true;
      conditionalAbortRef.current?.abort();
      conditionalAbortRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passkeySupported, authView]);

  const isLoading =
    login.isPending ||
    googleAuth.isPending ||
    githubAuth.isPending ||
    microsoftAuth.isPending ||
    forgotPassword.isPending ||
    initiateSSO.isPending ||
    magicLink.isPending ||
    passkeyAuth.isPending;

  // SSO domain auto-detection — 400ms debounce after typing a valid email.
  // Stale-result guard: the in-flight domain is captured in a closure;
  // results are dropped if the user has since changed the domain OR if
  // the component has unmounted between mutate-fire and response.
  useEffect(() => {
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      setSsoHint(null);
      ssoHintDomainRef.current = null;
      return;
    }
    const domain = trimmed.split("@")[1]?.toLowerCase();
    if (!domain || ssoHintDomainRef.current === domain) return;

    let mounted = true;
    const timer = setTimeout(() => {
      ssoHintDomainRef.current = domain;
      verifyDomain.mutate(
        { domain },
        {
          onSuccess: (data) => {
            if (!mounted) return;
            // Drop result if the user has typed a different domain since.
            if (ssoHintDomainRef.current !== domain) return;
            if (data.ssoEnabled && data.providers && data.providers.length > 0) {
              setSsoHint({
                providers: data.providers.map((p) => ({ id: p.id, name: p.name })),
              });
            } else {
              setSsoHint(null);
            }
          },
          onError: () => {
            if (!mounted) return;
            if (ssoHintDomainRef.current !== domain) return;
            setSsoHint(null);
          },
        },
      );
    }, 400);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
    // verifyDomain.mutate is stable across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Helper — clear `?error=`, `?ssoMfa=1`, `?slo=ok` once acted upon so the
  // banner does not re-appear on refresh.
  const clearTransientUrlErrors = () => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (next.has("error")) { next.delete("error"); changed = true; }
    if (next.has("ssoMfa")) { next.delete("ssoMfa"); changed = true; }
    if (next.has("slo")) { next.delete("slo"); changed = true; }
    if (changed) setSearchParams(next, { replace: true });
  };

  // Surface ?error= from the backend OIDC/SAML callback once on mount.
  // Falls back to a generic recovery message if the backend ships a new
  // error code that the frontend dictionary has not yet learned.
  useEffect(() => {
    const errorCode = searchParams.get("error");
    if (!errorCode) return;
    setError(
      SSO_ERROR_MESSAGES[errorCode] ||
        t("login.errors.ssoCallbackGeneric"),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSO + MFA two-step flow — backend sets a short-lived cookie and
  // redirects to /login?ssoMfa=1. Exchange the cookie for a tempToken.
  useEffect(() => {
    if (searchParams.get("ssoMfa") !== "1") return;
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = (import.meta.env.VITE_API_URL as string) || "https://api.adjudon.com";
        const res = await fetch(`${baseUrl}/api/auth/sso/mfa-exchange`, { credentials: "include" });
        if (cancelled) return;
        if (!res.ok) {
          setError(t("login.errors.ssoSessionExpiredMfa"));
          return;
        }
        const body = await res.json();
        const tt = body?.data?.tempToken;
        if (!tt) {
          setError(t("login.errors.ssoMfaNoToken"));
          return;
        }
        setTempToken(tt);
        setMfaCode("");
        setMfaError("");
        setAuthView("mfa-required");
      } catch {
        if (!cancelled) setError(t("login.errors.ssoMfaStart"));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slo-success toast.
  useEffect(() => {
    const slo = searchParams.get("slo");
    if (slo === "ok") toast.success(t("login.toasts.ssoSloOk"));
    else if (slo === "error") toast.error(t("login.toasts.ssoSloError"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus the MFA code field when the view opens.
  useEffect(() => {
    if (authView !== "mfa-required") return;
    const id = window.setTimeout(() => mfaInputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [authView]);

  // ── Handlers ──────────────────────────────────────────────────────

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    // User chose password — cancel the parallel passkey-autofill prompt
    // so both flows don't race for the JWT swap.
    conditionalAbortRef.current?.abort();
    conditionalAbortRef.current = null;
    setError("");
    setIsAccountLocked(false);
    setUnlockEmailSent(false);
    clearTransientUrlErrors();

    if (!email || !isValidEmail(email)) {
      setError(t("login.errors.emailRequired"));
      return;
    }
    if (!password) {
      setError(t("login.errors.passwordRequired"));
      return;
    }

    try {
      const response = await login.mutateAsync({ email, password });
      if (response.requiresMfa && response.tempToken) {
        setTempToken(response.tempToken);
        setMfaCode("");
        setMfaError("");
        setAuthView("mfa-required");
        return;
      }
      toast.success(t("login.toasts.signedIn"), { description: t("login.toasts.signedInDescription") });
      navigate("/dashboard");
    } catch (err) {
      const lowered = err instanceof Error ? err.message.toLowerCase() : "";
      if (lowered.includes("locked") || lowered.includes("too many failed") || lowered.includes("account_locked")) {
        setIsAccountLocked(true);
        setError(t("login.errors.accountLocked"));
      } else if (
        lowered.includes("password") ||
        lowered.includes("not found") ||
        lowered.includes("no account") ||
        lowered.includes("invalid credentials")
      ) {
        setError(t("login.errors.credentialsMismatch"));
      } else if (lowered.includes("sso required") || lowered.includes("workspace requires")) {
        setError(t("login.errors.ssoRequired"));
      } else if (lowered.includes("disabled") || lowered.includes("revoked")) {
        setError(t("login.errors.accessDisabled"));
      } else if (lowered.includes("trial") || lowered.includes("inactive")) {
        setError(t("login.errors.workspaceInactive"));
      } else {
        setError(t("login.errors.generic"));
      }
    }
  };

  const handleSendMagicLink = async () => {
    setError("");
    clearTransientUrlErrors();
    if (!email || !isValidEmail(email)) {
      setError(t("login.errors.emailRequired"));
      return;
    }
    try {
      await magicLink.mutateAsync({ email });
      toast.success(t("login.toasts.magicLinkSent"), { description: t("login.toasts.magicLinkSentDescription", { email }) });
      setAuthView("magic-link-sent");
    } catch {
      setError(t("login.errors.magicLinkFailed"));
    }
  };

  const handleForgotPassword = async () => {
    setError("");
    clearTransientUrlErrors();
    if (!email || !isValidEmail(email)) {
      setError(t("login.errors.forgotEmailRequired"));
      return;
    }
    try {
      await forgotPassword.mutateAsync({ email });
      toast.success(t("login.toasts.resetSent"), { description: t("login.toasts.resetSentDescription", { email }) });
      setAuthView("reset-password-sent");
    } catch (err) {
      const lowered = err instanceof Error ? err.message.toLowerCase() : "";
      if (lowered.includes("not found") || lowered.includes("no account")) {
        toast.success(t("login.toasts.resetSent"), { description: t("login.toasts.resetSentDescription", { email }) });
        setAuthView("reset-password-sent");
      } else {
        setError(t("login.errors.resetFailed"));
      }
    }
  };

  const handleRequestUnlock = async () => {
    if (!email || !isValidEmail(email)) {
      setError(t("login.errors.unlockEmailRequired"));
      return;
    }
    try {
      await requestUnlock.mutateAsync(email);
      setUnlockEmailSent(true);
      toast.success(t("login.toasts.unlockSent"), {
        description: t("login.toasts.unlockSentDescription", { email }),
      });
    } catch {
      toast.error(t("login.toasts.unlockFailedTitle"), {
        description: t("login.toasts.unlockFailedDescription"),
      });
    }
  };

  const handlePasskeyLogin = async () => {
    if (isLoading) return;
    setError("");
    clearTransientUrlErrors();
    // Cancel the pending conditional-UI promise so the browser's autofill
    // chooser does not race with the explicit Touch-ID / Face-ID prompt.
    conditionalAbortRef.current?.abort();
    conditionalAbortRef.current = null;
    try {
      const response = await passkeyAuth.mutateAsync();
      // Backend may signal that MFA is still required after a passkey
      // login (e.g. step-up requested by risk-scoring). Match the same
      // branch as password-login.
      const requiresMfa = (response as { requiresMfa?: boolean })?.requiresMfa;
      const tempT = (response as { tempToken?: string })?.tempToken;
      if (requiresMfa && tempT) {
        setTempToken(tempT);
        setMfaCode("");
        setMfaError("");
        setAuthView("mfa-required");
        return;
      }
      toast.success(t("login.toasts.signedIn"), { description: t("login.toasts.signedInDescription") });
      navigate("/dashboard");
    } catch (err) {
      // NotAllowedError / AbortError fire when the user cancels the
      // biometric dialog or has no credential — silent failure is the
      // correct UX (no scary banner for cancelled actions).
      const name = (err as { name?: string } | null | undefined)?.name || "";
      if (name === "NotAllowedError" || name === "AbortError") return;
      const message = err instanceof Error ? err.message.toLowerCase() : "";
      if (message.includes("no credentials") || message.includes("not found")) {
        setError(t("login.errors.passkeyNoCredential"));
      } else if (message.includes("cancel") || message.includes("aborted")) {
        // Belt-and-suspenders: some browsers wrap cancellation as a
        // generic Error rather than NotAllowedError.
        return;
      } else {
        setError(t("login.errors.passkeyGeneric"));
      }
    }
  };

  const handleSSOLogin = () => {
    setError("");
    clearTransientUrlErrors();
    if (!email || !isValidEmail(email)) {
      setError(t("login.errors.ssoEmailRequired"));
      return;
    }
    initiateSSO.mutate(
      { email },
      {
        onSuccess: (data) => { window.location.href = data.authUrl; },
        onError: (err) => {
          const msg = err instanceof Error ? err.message.toLowerCase() : "";
          if (msg.includes("not configured") || msg.includes("no sso")) {
            setError(t("login.errors.ssoNotConfigured"));
          } else {
            setError(t("login.errors.ssoGeneric"));
          }
        },
      },
    );
  };

  // ── MFA handlers ──────────────────────────────────────────────────

  const handleVerify2FA = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setMfaError("");

    const trimmed = mfaCode.trim();
    if (!trimmed) {
      setMfaError(t("login.errors.mfaEmptyCode"));
      return;
    }
    const isTotp = /^\d{6}$/.test(trimmed);
    const isBackup = /^ADJD-[A-F0-9]{8}$/i.test(trimmed);
    if (!isTotp && !isBackup) {
      setMfaError(t("login.errors.mfaInvalidFormat"));
      return;
    }
    if (!tempToken) {
      setMfaError(t("login.errors.mfaExpiredSession"));
      return;
    }

    try {
      await verify2FA.mutateAsync({ tempToken, code: trimmed });
      toast.success(t("login.toasts.mfaVerified"), { description: t("login.toasts.signedInDescription") });
      navigate("/dashboard");
    } catch (err) {
      const msg = err instanceof Error ? err.message.toLowerCase() : "";
      if (msg.includes("expired") || msg.includes("invalid token") || msg.includes("token type")) {
        setMfaError(t("login.errors.mfaExpiredSession"));
      } else if (msg.includes("backup") && msg.includes("used")) {
        setMfaError(t("login.errors.mfaBackupUsed"));
      } else if (msg.includes("invalid") || msg.includes("2fa")) {
        setMfaError(t("login.errors.mfaMismatch"));
      } else {
        setMfaError(t("login.errors.mfaGeneric"));
      }
    }
  };

  const handleResend2FA = async () => {
    try {
      const result = await resend2FA.mutateAsync();
      const msg =
        (result as { message?: string } | null | undefined)?.message ||
        t("login.toasts.mfaHintDescription");
      toast.success(t("login.toasts.mfaHintTitle"), { description: msg });
    } catch {
      toast.message(t("login.toasts.mfaHintTitle"), {
        description: t("login.toasts.mfaHintDescription"),
      });
    }
  };

  const handleCancelMfa = () => {
    if (verify2FA.isPending) return;
    setTempToken(null);
    setMfaCode("");
    setMfaError("");
    setAuthView("default");
  };

  // ── Inline resends for the success-confirmation views ──────────────
  //
  // These wrappers exist because ResendButton awaits `onSend()` and starts
  // its 30s cooldown only on resolve. We rethrow the underlying mutation
  // error so ResendButton can suppress the cooldown — but we also keep
  // any rejection inside a try/catch so an unexpected backend response
  // does not propagate to window.onerror as an unhandled rejection.

  const handleResendMagicLink = async () => {
    try {
      await magicLink.mutateAsync({ email });
    } catch {
      // Cooldown is intentionally skipped so user can retry immediately.
    }
  };
  const handleResendReset = async () => {
    try {
      await forgotPassword.mutateAsync({ email });
    } catch {
      // Cooldown is intentionally skipped so user can retry immediately.
    }
  };

  // ── Render ─────────────────────────────────────────────────────────

  // No brand panel — pure utility-style auth surface (Mercury / Linear /
  // Vercel / Plaid / Cloudflare convention). The user has already decided
  // to sign in; this page is purely a credential-collection moment. Trust
  // signals live on the Trust Center and dashboard footer, not on the
  // auth surface.

  const headerAction = (
    <span className="text-sm text-[var(--muted-foreground)]">
      {t("login.headerActionPrefix")}{" "}
      <Link
        to="/signup"
        className="text-[var(--foreground)] hover:text-[var(--muted-foreground)] transition-colors motion-reduce:transition-none font-medium underline underline-offset-2"
      >
        {t("login.headerActionLink")}
      </Link>
    </span>
  );

  return (
    <AuthShell
      pageTitle={t("login.pageTitle")}
      pageDescription={t("login.pageDescription")}
      headerAction={headerAction}
      wide={authView === "default"}
    >
      <Crossfade stateKey={authView} focusOnEnter="h1">
        {authView === "default" && (
          <div className="space-y-8">
            <header className="text-center">
              <h1 className="editorial-display">
                {t("login.title")}
              </h1>
              <p className="editorial-subtitle">
                {t("login.subtitle")}
              </p>
            </header>

            {error && (
              <AuthBanner variant="error">
                <p>{error}</p>
                {isAccountLocked && !unlockEmailSent && (
                  <AuthButton
                    variant="secondary"
                    onClick={handleRequestUnlock}
                    disabled={requestUnlock.isPending || !isValidEmail(email)}
                    loading={requestUnlock.isPending}
                    loadingLabel={t("login.unlockSendLoading")}
                    className="mt-2.5"
                  >
                    {t("login.unlockSend")}
                  </AuthButton>
                )}
                {isAccountLocked && unlockEmailSent && (
                  <p
                    className="auth-mono-label mt-2"
                    style={{ color: "var(--confidence)" }}
                  >
                    {t("login.unlockSent")}
                  </p>
                )}
              </AuthBanner>
            )}

            <div className="auth-split">
              {/* LEFT — email + password */}
              <form onSubmit={handlePasswordLogin} className="space-y-4" noValidate>
                <AuthInput
                  label={t("login.emailLabel")}
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("login.emailPlaceholder")}
                  // "email webauthn" enables conditional-mediation
                  // passkey autofill on browsers that support it
                  // (Safari 16+, Chrome 108+). Falls back to plain email
                  // autofill everywhere else.
                  autoComplete="email webauthn"
                  inputMode="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  disabled={isLoading}
                  leadingIcon={<Mail className="w-4 h-4" aria-hidden="true" />}
                />

                <AuthInput
                  label={t("login.passwordLabel")}
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("login.passwordPlaceholder")}
                  autoComplete="current-password"
                  disabled={isLoading}
                  leadingIcon={<Lock className="w-4 h-4" aria-hidden="true" />}
                  withPasswordToggle
                  labelAction={
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      disabled={isLoading}
                      className="auth-tertiary"
                      style={{ fontSize: 12 }}
                    >
                      {t("login.forgotPassword")}
                    </button>
                  }
                />

                <AuthButton
                  type="submit"
                  variant="primary"
                  fullWidth
                  loading={login.isPending}
                  loadingLabel={t("login.submitLoading")}
                  disabled={isLoading}
                >
                  {t("login.submit")}
                </AuthButton>

                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={handleSendMagicLink}
                    disabled={isLoading || magicLink.isPending}
                    className="auth-tertiary inline-flex items-center gap-1.5"
                  >
                    {magicLink.isPending ? t("login.magicLinkSending") : t("login.magicLinkCta")}
                  </button>
                </div>
              </form>

              {/* Divider (only visible on lg+) */}
              <div className="auth-split-divider" aria-hidden="true" />

              {/* RIGHT — Passkey, OAuth + SSO */}
              <div className="space-y-3">
                {passkeySupported && (
                  <button
                    type="button"
                    onClick={handlePasskeyLogin}
                    disabled={isLoading}
                    aria-busy={passkeyAuth.isPending || undefined}
                    aria-label={t("login.passkeyCta")}
                    className="adp-btn w-full inline-flex items-center justify-center gap-2.5 min-h-[44px]"
                  >
                    <span className="inline-flex items-center text-[var(--muted-foreground)]">
                      <KeyRound className="w-4 h-4" aria-hidden="true" />
                    </span>
                    <span>{passkeyAuth.isPending ? t("login.passkeyLoading") : t("login.passkeyCta")}</span>
                  </button>
                )}

                <OAuthButtonRow
                  onGoogle={() => googleAuth.mutate()}
                  onGithub={() => githubAuth.mutate()}
                  onMicrosoft={() => microsoftAuth.mutate()}
                  onSSO={handleSSOLogin}
                  loading={{
                    google: googleAuth.isPending,
                    github: githubAuth.isPending,
                    microsoft: microsoftAuth.isPending,
                    sso: initiateSSO.isPending,
                    any: isLoading,
                  }}
                  ssoProviderName={ssoHint?.providers[0]?.name ?? null}
                  verb="sign in"
                />

                {ssoHint ? (
                  <AuthBanner variant="success" icon={ShieldCheck}>
                    <span className="font-medium">{t("login.ssoAvailable")}</span>
                    {" "}{t("login.ssoAvailableSuffix", { providers: ssoHint.providers.map((p) => p.name).join(", ") })}
                  </AuthBanner>
                ) : verifyDomain.isPending ? (
                  <p className="auth-helper" aria-live="polite">
                    {t("login.ssoChecking")}
                  </p>
                ) : (
                  <p className="auth-helper">
                    {t("login.ssoHelper")}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {authView === "magic-link-sent" && (
          <div className="space-y-6">
            <header>
              <h1 className="editorial-display">{t("login.magicLinkSentTitle")}</h1>
              <p className="editorial-subtitle">
                <Trans
                  i18nKey="login.magicLinkSentBody"
                  values={{ email }}
                  components={[<span key="email" className="font-medium text-[var(--foreground)]" />]}
                />
              </p>
            </header>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[var(--border)]">
              <ResendButton onSend={handleResendMagicLink} label={t("login.magicLinkResend")} />
              <button
                type="button"
                onClick={() => setAuthView("default")}
                className="auth-tertiary"
              >
                {t("login.magicLinkUsePassword")}
              </button>
            </div>
          </div>
        )}

        {authView === "reset-password-sent" && (
          <div className="space-y-6">
            <header>
              <h1 className="editorial-display">{t("login.resetSentTitle")}</h1>
              <p className="editorial-subtitle">
                <Trans
                  i18nKey="login.resetSentBody"
                  values={{ email }}
                  components={[<span key="email" className="font-medium text-[var(--foreground)]" />]}
                />
              </p>
            </header>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[var(--border)]">
              <ResendButton onSend={handleResendReset} label={t("login.magicLinkResend")} />
              <button
                type="button"
                onClick={() => setAuthView("default")}
                className="auth-tertiary"
              >
                {t("login.magicLinkUsePassword")}
              </button>
            </div>
          </div>
        )}

        {authView === "mfa-required" && (
          <div className="space-y-6">
            <header>
              <h1 className="editorial-display">{t("login.mfaTitle")}</h1>
              <p className="editorial-subtitle">
                <Trans
                  i18nKey="login.mfaSubtitle"
                  values={{ email }}
                  components={[
                    <span key="email" className="font-medium text-[var(--foreground)]" />,
                    <span key="mono" style={{ fontFamily: "var(--font-mono)" }} />,
                  ]}
                />
              </p>
            </header>

            <form onSubmit={handleVerify2FA} className="space-y-4" noValidate>
              {mfaError && (
                <AuthBanner variant="error" id={mfaErrorId}>
                  {mfaError}
                </AuthBanner>
              )}

              <AuthInput
                ref={mfaInputRef}
                label={t("login.mfaCodeLabel")}
                type="text"
                name="mfa-code"
                inputMode="text"
                autoComplete="one-time-code"
                autoCapitalize="characters"
                spellCheck={false}
                value={mfaCode}
                onChange={(e) =>
                  setMfaCode(e.target.value.toUpperCase().replace(/\s+/g, ""))
                }
                placeholder="000000"
                maxLength={13}
                disabled={verify2FA.isPending}
                style={{ fontFamily: "var(--font-mono)", letterSpacing: "0.08em", textAlign: "center", fontSize: 16 }}
                aria-describedby={mfaError ? mfaErrorId : undefined}
                helper={t("login.mfaCodeHelper")}
              />

              <AuthButton
                type="submit"
                variant="primary"
                fullWidth
                loading={verify2FA.isPending}
                loadingLabel={t("login.mfaSubmitLoading")}
                disabled={!mfaCode.trim() || verify2FA.isPending}
              >
                {t("login.mfaSubmit")}
              </AuthButton>
            </form>

            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-[var(--border)]">
              <button
                type="button"
                onClick={handleResend2FA}
                disabled={resend2FA.isPending || verify2FA.isPending}
                className="auth-tertiary"
              >
                {resend2FA.isPending ? t("login.mfaResendLoading") : t("login.mfaResend")}
              </button>
              <button
                type="button"
                onClick={handleCancelMfa}
                disabled={verify2FA.isPending}
                className="auth-tertiary"
              >
                {t("login.mfaCancel")}
              </button>
            </div>
          </div>
        )}
      </Crossfade>
    </AuthShell>
  );
}
